import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';

const Loader = (Component: any) => (props: any) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Pages

const Overview = Loader(lazy(() => import('src/content/overview')));

// Dashboards

const Home = Loader(lazy(() => import('src/content/dashboards/Home')));
const SuperAdminDashboard = Loader(lazy(() => import('src/content/dashboards/SuperAdmin')));

// Applications

const Messenger = Loader(
  lazy(() => import('src/content/applications/Messenger'))
);

const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);
const ManagementTenants = Loader(
  lazy(() => import('src/content/applications/Tenants'))
);
const ManagementUsers = Loader(
  lazy(() => import('src/content/applications/Users/management/ManagementPage'))
);
const UserReport = Loader(
  lazy(() => import('src/content/applications/Users/management/UserReport'))
);
const ManagementOrgUnits = Loader(
  lazy(() => import('src/content/applications/OrgUnits'))
);
const ManagementJobTitles = Loader(
  lazy(() => import('src/content/applications/JobTitles'))
);
const SystemSettings = Loader(
  lazy(() => import('src/content/applications/SystemSettings'))
);
const ManagementPrompts = Loader(
  lazy(() => import('src/content/applications/Prompts'))
);

const ContentFactory = Loader(
  lazy(() => import('src/features/content-factory'))
);
const MicroLessons = Loader(
  lazy(() => import('src/features/micro-lessons'))
);
const MicroLessonsClient = Loader(
  lazy(() => import('src/features/micro-lessons/client'))
);

const DailyRefresh = Loader(
  lazy(() => import('src/features/learning'))
);

const FlashcardsPage = Loader(
  lazy(() => import('src/features/learning/FlashcardsPage'))
);

const ReflectionCoach = Loader(
  lazy(() => import('src/features/reflection'))
);

const TheOracle = Loader(
  lazy(() => import('src/features/oracle'))
);

const ActiveSimulations = Loader(
  lazy(() => import('src/features/simulations'))
);

const SimulationReport = Loader(
  lazy(() => import('./features/simulations/SimulationReport'))
);

const GapAnalysisRoadmap = Loader(
  lazy(() => import('src/features/analytics'))
);

const AnalyticsDashboard = Loader(
  lazy(() => import('src/features/analytics/AnalyticsDashboard'))
);

const LMSClone = Loader(
  lazy(() => import('src/content/applications/LMSClone'))
);



// Components

const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status

const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const Login = Loader(lazy(() => import('src/content/pages/Auth/Login')));

const routes: RouteObject[] = [
  {
    path: '',
    element: (
      <GuestGuard>
        <BaseLayout />
      </GuestGuard>
    ),
    children: [
      {
        path: '/',
        element: <Login />
      },
      {
        path: 'login',
        element: <Navigate to="/" replace />
      },
      {
        path: 'overview',
        element: <Overview />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboards',
    element: (
      <AuthGuard>
        <SidebarLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="home" replace />
      },
      {
        path: 'home',
        element: <Home />
      },
      {
        path: 'super-admin',
        element: <SuperAdminDashboard />
      },
      {
        path: 'messenger',
        element: <Messenger />
      }
    ]
  },
  {
    path: 'management',
    element: (
      <AuthGuard>
        <SidebarLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="users" replace />
      },

      {
        path: 'tenants',
        element: <ManagementTenants />
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            element: <ManagementUsers />
          },
          {
            path: 'report/:userId',
            element: <UserReport />
          }
        ]
      },
      {
        path: 'org-units',
        element: <ManagementOrgUnits />
      },
      {
        path: 'job-titles',
        element: <ManagementJobTitles />
      },
      {
        path: 'system-settings',
        element: <SystemSettings />
      },
      {
        path: 'prompts',
        element: <ManagementPrompts />
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  },
  {
    path: 'trainings',
    element: (
      <AuthGuard>
        <SidebarLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="content-factory" replace />
      },
      {
        path: 'content-factory',
        element: <ContentFactory />
      },
      {
        path: 'micro-lessons',
        element: <MicroLessons />
      },
      {
        path: 'micro-lessons-player',
        element: <MicroLessonsClient />
      },
      {
        path: 'flashcards',
        element: <FlashcardsPage />
      },
      {
        path: 'daily-refresh',
        element: <DailyRefresh />
      },
      {
        path: 'reflection',
        element: <ReflectionCoach />
      },
      {
        path: 'oracle',
        element: <TheOracle />
      },
      {
        path: 'simulations',
        element: <ActiveSimulations />
      },
      {
        path: 'simulations/report/:sessionId',
        element: <SimulationReport />
      },
      {
        path: 'roadmap',
        element: <GapAnalysisRoadmap />
      },
      {
        path: 'analytics-dashboard',
        element: <AnalyticsDashboard />
      },
      {
        path: 'lms-clone',
        element: <LMSClone />
      }
    ]
  },
  {

    path: '/components',
    element: (
      <AuthGuard>
        <SidebarLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  }
];

export default routes;
